(() => {
  const root = document.querySelector('[data-booking]');
  if (!root) return;
  if (root.dataset.bookingInit === '1') return;
  root.dataset.bookingInit = '1';

  const URL_PARAM_GROUP = 'group';
  const URL_PARAM_STEP = 'step';
  const URL_PARAM_SERVICE = 'service';
  const URL_PARAM_VARIANT = 'variant';
  const URL_PARAM_ITEMS = 'items'; // urlKey:qty,urlKey:qty
  const URL_PARAM_VARIANTS = 'variants'; // variantId:qty,variantId:qty

  const safeDecode = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const normalizeKey = (value) =>
    safeDecode(String(value || ''))
      .replace(/^#/, '')
      .trim()
      .toLowerCase();

  let step = 1;
  let activeTab = null;
  const selections = new Map(); // variantId -> { qty, price, title, serviceId, urlKey }
  const controlsByVariant = new Map(); // variantId -> { setChecked, setQty, urlKey, tab }
  const tabButtons = Array.from(root.querySelectorAll('.tab-btn'));
  const anchorToTab = new Map();
  const tabToAnchor = new Map();
  const urlKeyToVariant = new Map();

  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const formatMoney = (n) => {
    const val = Number(n || 0).toFixed(2);
    return moneyFormat.replace('{{amount}}', val).replace('${{amount}}', `$${val}`);
  };

  const getStepSlug = (n) => (Number(n) === 2 ? 'checkout' : 'select');

  const pushDataLayer = (payload) => {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push(payload);
  };

  const emitEvent = (name, detail) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch {
      // no-op
    }
  };

  let suspendUrlSync = false;

  const getLineItemProperties = (data) => ({
    _booking_service_id: String(data?.serviceId || ''),
    _booking_service_key: String(data?.urlKey || ''),
    _booking_step: getStepSlug(step),
    _booking_url: window.location.pathname + window.location.search
  });

  const getCartAttributes = () => {
    const attributionKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid'];
    const url = new URL(window.location.href);
    const attrs = {
      booking_step: getStepSlug(step),
      booking_items: Array.from(selections.values())
        .map((s) => `${normalizeKey(s.urlKey || s.serviceId)}:${s.qty}`)
        .join(','),
      booking_url: window.location.href
    };
    attributionKeys.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) attrs[`booking_${k}`] = v;
    });
    return attrs;
  };

  const updateUrlFromState = ({ push = false } = {}) => {
    if (suspendUrlSync) return;
    const url = new URL(window.location.href);
    const sp = url.searchParams;

    const groupAnchor = tabToAnchor.get(activeTab) || '';
    if (groupAnchor) sp.set(URL_PARAM_GROUP, groupAnchor);
    else sp.delete(URL_PARAM_GROUP);

    sp.set(URL_PARAM_STEP, getStepSlug(step));

    const itemPairs = [];
    selections.forEach((data, variantId) => {
      const qty = Number(data.qty || 0);
      if (!qty) return;
      const key = normalizeKey(data.urlKey || data.serviceId || '');
      if (key) itemPairs.push(`${key}:${qty}`);
    });

    if (itemPairs.length) sp.set(URL_PARAM_ITEMS, itemPairs.join(','));
    else sp.delete(URL_PARAM_ITEMS);

    if (selections.size === 1) {
      const [onlyVariantId] = selections.keys();
      const data = selections.get(onlyVariantId);
      const onlyKey = normalizeKey(data?.urlKey || data?.serviceId || '');
      const qty = Number(data?.qty || 1);
      sp.delete(URL_PARAM_ITEMS);
      if (onlyKey) sp.set(URL_PARAM_SERVICE, onlyKey);
      else sp.delete(URL_PARAM_SERVICE);
      if (onlyKey && qty > 1) sp.set(URL_PARAM_ITEMS, `${onlyKey}:${qty}`);
    } else {
      sp.delete(URL_PARAM_SERVICE);
    }

    // Booking URLs should stay service-key centric; keep reading these for backward compatibility but don't emit them.
    sp.delete(URL_PARAM_VARIANT);
    sp.delete(URL_PARAM_VARIANTS);

    url.hash = '';
    const next = `${url.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`;
    try {
      history[push ? 'pushState' : 'replaceState'](null, '', next);
    } catch {
      // no-op
    }
  };

  const updateTotals = () => {
    const total = Array.from(selections.values()).reduce((sum, item) => sum + item.price * item.qty, 0);
    root.querySelectorAll('[data-total]').forEach((el) => (el.textContent = formatMoney(total)));
    const nextBtn = root.querySelector('[data-step="1"] [data-next]');
    if (nextBtn) nextBtn.disabled = selections.size === 0;
  };

  const updateSummary = () => {
    const summary = root.querySelector('[data-summary]');
    if (summary) {
      summary.innerHTML = '';
      selections.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'service-row flex items-start justify-between gap-4';
        row.innerHTML = `
          <div class="text-left">
            <div class="text-white font-semibold">${item.title}</div>
            <div class="text-slate-400 text-sm">Qty: ${item.qty} × ${formatMoney(item.price)}</div>
          </div>
          <div class="text-blue-400 font-semibold whitespace-nowrap">${formatMoney(item.qty * item.price)}</div>
        `;
        summary.appendChild(row);
      });
    }
  };

  const setStep = (nextStep, { push = false } = {}) => {
    step = nextStep;
    root.querySelectorAll('.booking-step').forEach((stepEl) => {
      stepEl.hidden = Number(stepEl.dataset.step) !== step;
    });
    root.querySelector('[data-step-label]').textContent = String(step);
    const titles = ['Select Your Services', 'Checkout'];
    const titleEl = root.querySelector('[data-step-title]');
    if (titleEl && titles[step - 1]) titleEl.textContent = titles[step - 1];
    root.querySelectorAll('[data-progress]').forEach((dot) => {
      const value = Number(dot.dataset.progress);
      dot.classList.toggle('active', value === step);
      dot.classList.toggle('done', value < step);
    });
    if (step === 2) updateSummary();

    updateUrlFromState({ push });
    pushDataLayer({
      event: 'booking_step',
      booking_step: getStepSlug(step),
      booking_items: Array.from(selections.values()).map((s) => ({ service: s.urlKey || s.serviceId, qty: s.qty }))
    });
    emitEvent('booking:step', { step: getStepSlug(step) });
  };

  const bindServices = () => {
    root.querySelectorAll('.service-row[data-variant]').forEach((row) => {
      const variantId = row.dataset.variant;
      const price = Number(row.dataset.price || 0);
      const title = row.dataset.title || '';
      const serviceId = row.dataset.serviceId || variantId;
      const urlKey = normalizeKey(row.dataset.urlKey || serviceId || variantId);
      const tab = row.closest('[data-tab-panel]')?.dataset.tabPanel ?? null;
      const checkbox = row.querySelector('.service-check');
      const qtyWrap = row.querySelector('.qty-control');
      const qtyDisplay = row.querySelector('[data-qty]');
      const inc = row.querySelector('[data-action="increment"]');
      const dec = row.querySelector('[data-action="decrement"]');
      const desc = row.querySelector('.service-desc');

      const setChecked = (checked) => {
        checkbox.checked = checked;
        qtyWrap.hidden = !checked;
        if (checked && !selections.has(variantId)) {
          selections.set(variantId, { qty: 1, price, title, serviceId, urlKey });
        } else if (!checked) {
          selections.delete(variantId);
        }
        updateTotals();
        updateUrlFromState({ push: false });
        emitEvent('booking:selection', { selections: Array.from(selections.values()) });
      };

      const setQty = (qty) => {
        const n = Number(qty);
        if (!Number.isFinite(n) || n <= 0) {
          setChecked(false);
          checkbox.checked = false;
          qtyDisplay.textContent = '1';
          return;
        }
        if (!checkbox.checked) setChecked(true);
        const current = selections.get(variantId);
        if (!current) return;
        current.qty = n;
        qtyDisplay.textContent = String(n);
        updateTotals();
        updateUrlFromState({ push: false });
      };

      controlsByVariant.set(variantId, { setChecked, setQty, urlKey, tab });
      if (urlKey && !urlKeyToVariant.has(urlKey)) urlKeyToVariant.set(urlKey, variantId);

      checkbox.addEventListener('change', () => setChecked(checkbox.checked));

      row.addEventListener('click', (e) => {
        if (e.target.closest('button') || e.target === checkbox) return;
        setChecked(!checkbox.checked);
      });

      inc?.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = selections.get(variantId);
        if (!current) return;
        setQty(current.qty + 1);
      });

      dec?.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = selections.get(variantId);
        if (!current) return;
        if (current.qty <= 1) {
          setChecked(false);
          checkbox.checked = false;
          qtyDisplay.textContent = '1';
          return;
        }
        setQty(current.qty - 1);
      });

      if (desc) {
        const btn = desc.querySelector('.read-more');
        const full = desc.dataset.full;
        const truncated = desc.dataset.truncated;
        const text = desc.querySelector('.desc-text') || desc.firstChild;
        if (btn && full && truncated && text) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const expanded = desc.dataset.state === 'expanded';
            desc.dataset.state = expanded ? 'collapsed' : 'expanded';
            text.textContent = expanded ? truncated : full;
            btn.textContent = expanded ? 'Read More' : 'Read Less';
          });
        }
      }
    });
  };

  const setActiveTab = (tab, { syncUrl = true } = {}) => {
    const activeClasses = ['bg-blue-900/60', 'text-blue-200', 'border-blue-700'];
    const inactiveClasses = ['border-slate-600', 'text-slate-300'];
    activeTab = tab;
    tabButtons.forEach((b) => {
      const isActive = b.dataset.tab === tab;
      b.classList.toggle('active', isActive);
      activeClasses.forEach((cls) => b.classList.toggle(cls, isActive));
      inactiveClasses.forEach((cls) => b.classList.toggle(cls, !isActive));
    });
    root.querySelectorAll('.service-tab').forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== tab;
    });
    if (syncUrl) updateUrlFromState({ push: false });
  };

  const bindTabs = () => {
    tabButtons.forEach((btn) => {
      const anchor = normalizeKey(btn.dataset.tabAnchor);
      if (anchor && !anchorToTab.has(anchor)) anchorToTab.set(anchor, btn.dataset.tab);
      if (anchor && !tabToAnchor.has(btn.dataset.tab)) tabToAnchor.set(btn.dataset.tab, anchor);
    });

    tabButtons.forEach((btn) => {
      btn.addEventListener('click', () => setActiveTab(btn.dataset.tab, { syncUrl: true }));
    });
  };

  const parsePairList = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return [];
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const [idRaw, qtyRaw] = part.split(':');
        const id = safeDecode(idRaw || '').trim();
        const qty = Math.max(1, Number(qtyRaw || 1));
        return { id, qty: Number.isFinite(qty) ? qty : 1 };
      });
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  };

  const syncCartToSelections = async ({ setAttributes = true } = {}) => {
    const cart = await fetchCart();

    const desiredQtyByVariant = new Map(
      Array.from(selections.entries()).map(([variantId, data]) => [String(variantId), Number(data.qty || 0)])
    );

    const serviceVariantIds = new Set(Array.from(controlsByVariant.keys()).map(String));
    const cartItems = Array.isArray(cart.items) ? cart.items : [];

    for (const item of cartItems) {
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId || !serviceVariantIds.has(variantId)) continue;
      const desired = Number(desiredQtyByVariant.get(variantId) || 0);
      const data = selections.get(variantId);
      const changeBody = {
        id: item.key,
        quantity: desired
      };
      if (desired > 0 && data) changeBody.properties = getLineItemProperties(data);
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changeBody)
      });
      if (!changeRes.ok) throw new Error('Cart update failed');
      desiredQtyByVariant.delete(variantId);
    }

    const itemsToAdd = [];
    desiredQtyByVariant.forEach((qty, variantId) => {
      if (!qty) return;
      const data = selections.get(String(variantId));
      if (!data) return;
      itemsToAdd.push({
        id: Number(variantId),
        quantity: qty,
        properties: getLineItemProperties(data)
      });
    });

    if (itemsToAdd.length) {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });
      if (!addRes.ok) throw new Error('Cart add failed');
    }

    if (setAttributes) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: getCartAttributes() })
        });
      } catch (e) {
        console.warn('Could not set booking cart attributes', e);
      }
    }
  };

  const hydrateFromCart = async () => {
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      console.warn('Could not hydrate booking from cart', e);
      return false;
    }

    const qtyByVariant = new Map();
    (cart.items || []).forEach((item) => {
      const variantId = String(item.variant_id || item.id || '');
      if (!variantId || !controlsByVariant.has(variantId)) return;
      const current = Number(qtyByVariant.get(variantId) || 0);
      qtyByVariant.set(variantId, current + Number(item.quantity || 0));
    });

    if (!qtyByVariant.size) return false;

    suspendUrlSync = true;
    qtyByVariant.forEach((qty, variantId) => {
      const control = controlsByVariant.get(String(variantId));
      if (control) control.setQty(qty);
    });
    suspendUrlSync = false;

    updateTotals();
    updateUrlFromState({ push: false });
    emitEvent('booking:cart_hydrate', { variants: Array.from(qtyByVariant.entries()) });
    return true;
  };

  const hydrateFromUrl = () => {
    const url = new URL(window.location.href);
    const sp = url.searchParams;

    const hashAnchor = normalizeKey(url.hash);
    const groupFromHash = hashAnchor && !sp.get(URL_PARAM_GROUP) ? hashAnchor : '';
    if (groupFromHash) sp.set(URL_PARAM_GROUP, groupFromHash);

    const group = normalizeKey(sp.get(URL_PARAM_GROUP));
    const tabFromGroup = group ? anchorToTab.get(group) : null;

    const variantPairs = parsePairList(sp.get(URL_PARAM_VARIANTS));
    const itemPairs = parsePairList(sp.get(URL_PARAM_ITEMS)).map((p) => ({ ...p, id: normalizeKey(p.id) }));
    const singleVariant = sp.get(URL_PARAM_VARIANT);
    const singleService = normalizeKey(sp.get(URL_PARAM_SERVICE));
    const hasAnySelectionParam = Boolean(variantPairs.length || itemPairs.length || singleVariant || singleService);
    let didApplySelection = false;

    const applyVariant = (variantId, qty) => {
      const control = controlsByVariant.get(String(variantId));
      if (!control) return false;
      control.setQty(qty);
      return true;
    };

    const applyServiceKey = (key, qty) => {
      const variantId = urlKeyToVariant.get(normalizeKey(key));
      if (!variantId) return false;
      return applyVariant(variantId, qty);
    };

    suspendUrlSync = true;
    if (tabFromGroup != null) setActiveTab(String(tabFromGroup), { syncUrl: false });

    if (variantPairs.length) {
      variantPairs.forEach(({ id, qty }) => {
        if (applyVariant(id, qty)) didApplySelection = true;
      });
    } else if (itemPairs.length) {
      itemPairs.forEach(({ id, qty }) => {
        if (applyServiceKey(id, qty)) didApplySelection = true;
      });
    } else if (singleVariant) {
      didApplySelection = applyVariant(singleVariant, 1);
    } else if (singleService) {
      didApplySelection = applyServiceKey(singleService, 1);
    }

    const firstSelection = selections.keys().next().value;
    if (activeTab == null && firstSelection) {
      const control = controlsByVariant.get(String(firstSelection));
      if (control?.tab != null) setActiveTab(String(control.tab), { syncUrl: false });
    }

    if (activeTab == null && tabButtons[0]) setActiveTab(tabButtons[0].dataset.tab, { syncUrl: false });

    const stepRaw = String(sp.get(URL_PARAM_STEP) || '').toLowerCase();
    const wantsCheckout = stepRaw === '2' || stepRaw === 'checkout';
    if (wantsCheckout && selections.size) setStep(2, { push: false });
    else setStep(1, { push: false });

    updateTotals();
    suspendUrlSync = false;
    updateUrlFromState({ push: false });

    return { hasAnySelectionParam, didApplySelection, wantsCheckout };
  };

  const bindNavigation = () => {
    let syncingOnNext = false;
    root.querySelectorAll('[data-next]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (step === 1 && selections.size === 0) return;
        if (step === 1 && !syncingOnNext) {
          syncingOnNext = true;
          const originalText = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Saving...';
          try {
            await syncCartToSelections({ setAttributes: true });
          } catch (e) {
            console.error(e);
            alert('We could not save your selected services to the cart. Please try again.');
            btn.disabled = false;
            btn.textContent = originalText;
            syncingOnNext = false;
            return;
          }
          btn.disabled = false;
          btn.textContent = originalText;
          syncingOnNext = false;
        }
        setStep(Math.min(2, step + 1), { push: true });
      });
    });
    root.querySelectorAll('[data-back]').forEach((btn) => {
      btn.addEventListener('click', () => setStep(Math.max(1, step - 1), { push: true }));
    });
  };

  const bindComplete = () => {
    const completeBtn = root.querySelector('[data-complete]');
    if (!completeBtn) return;
    completeBtn.addEventListener('click', async () => {
      if (!selections.size) return;
      completeBtn.disabled = true;
      completeBtn.textContent = 'Processing...';

      try {
        await syncCartToSelections({ setAttributes: true });

        window.location.href = '/checkout';
      } catch (err) {
        console.error(err);
        alert(`There was a problem completing your booking. ${err.message || ''}`.trim());
        completeBtn.disabled = false;
        completeBtn.textContent = 'Complete Booking';
      }
    });
  };

  bindTabs();
  bindServices();
  const urlHydration = hydrateFromUrl();
  hydrateFromCart().then(() => {
    if (urlHydration.wantsCheckout && selections.size) setStep(2, { push: false });
  });
  bindNavigation();
  bindComplete();
})(); 
